import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { getDateRangeBounds } from '../../../contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import { GET_REGION_RECRUIT_STATS_QUERY } from '../../../graphql/queries';
import { DateRangeType } from '../../../types/phlebotomist-dashboard';
import { IRegionRecruitStats } from '../../cards/RecruiterStatsCard';

interface IUseRegionRecruitStatsParams {
  dateRange: DateRangeType;
  regionIds: string[];
  activeLocationThresholdDays: number;
  skip: boolean;
}

export function useRegionRecruitStats({
  dateRange,
  regionIds,
  activeLocationThresholdDays,
  skip,
}: IUseRegionRecruitStatsParams): IRegionRecruitStats[] {
  const bounds = useMemo(() => {
    const current = getDateRangeBounds(dateRange);
    const durationMs = current.end.getTime() - current.start.getTime();
    const prevEnd = new Date(current.start.getTime() - 1);
    const prevStart = new Date(prevEnd.getTime() - durationMs);
    return { current, prevStart, prevEnd };
  }, [dateRange]);

  const { data } = useQuery(GET_REGION_RECRUIT_STATS_QUERY, {
    variables: {
      input: {
        start: bounds.current.start.toISOString(),
        end: bounds.current.end.toISOString(),
        prevStart: bounds.prevStart.toISOString(),
        prevEnd: bounds.prevEnd.toISOString(),
        activeLocationThresholdDays,
        regionIds: regionIds.length > 0 ? regionIds : undefined,
      },
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  return useMemo(
    () =>
      (data?.getRegionRecruitStats?.entries ?? []).map(
        (e: {
          regionId: string;
          regionName: string;
          recruitedDonorCount: number;
          prevRecruitedDonorCount: number;
          activeLocationCount: number;
          totalLocationCount: number;
        }) => ({
          regionId: e.regionId,
          regionName: e.regionName,
          recruitedDonorCount: e.recruitedDonorCount,
          prevRecruitedDonorCount: e.prevRecruitedDonorCount,
          activeLocationCount: e.activeLocationCount,
          totalLocationCount: e.totalLocationCount,
        })
      ),
    [data]
  );
}
